import { Component, OnInit, inject, NgZone, signal } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialog } from '@angular/material/dialog';
import { Observable, combineLatest, of, merge, timer } from 'rxjs';
import { map, switchMap, filter, startWith, shareReplay, take, distinctUntilChanged, tap, delay } from 'rxjs/operators';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { LoggerService } from '@app/core/infrastructure';
import { MetaAnalyticsService } from '@app/core/infrastructure';
import { AvatarBoxMode, AvatarBoxFullState } from '@app/store/audio/models/audio-state.models';
import { FeedbackInfrastructureService } from '@app/core/infrastructure';
import { UserFacade } from '@app/store/user/user.facade';
import { NotificationFacade } from '@app/store/notification';
import { NotificationPayload } from '@app/core/infrastructure/notification';
import { NotificationService } from '@app/core/infrastructure/notification';
import { StripeFacade } from '@app/store/stripe';
import { UserSubscription } from '@app/core/api/stripe';
import { ToastNotificationService } from '@app/core/infrastructure';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent, ConfirmationDialogData } from './popups/confirmation-dialog/confirmation-dialog.component';
import { SessionEndedDialogComponent, SessionEndedDialogData, SessionEndedDialogResult } from './popups/session-ended-dialog/session-ended-dialog.component';
import { NotificationActionDialogComponent, NotificationActionDialogData, NotificationActionDialogResult } from './popups/notification-action-dialog/notification-action-dialog.component';
import { ActivateSubscriptionDialogComponent, ActivateSubscriptionDialogData, ActivateSubscriptionDialogResult } from './popups/activate-subscription-dialog/activate-subscription-dialog.component';
import { AvatarFacade } from '@app/store/avatar/avatar.facade';
import { VersionService } from '@app/core/infrastructure';
import { UiFacade } from '@app/store/ui';
import { AudioFacade } from '@app/store/audio/audio.facade';
import { AudioSessionManagerService } from '@app/core/infrastructure/audio';
import { StatusBarService } from '@app/core/infrastructure';
import { AuthFacade } from '@app/store/auth/auth.facade';
import { PUBLIC_ROUTES } from '@app/store/router/router.selectors';
import { AppleIapService } from '@app/core/infrastructure/payment/apple-iap.service';

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
    private readonly logger = inject(LoggerService).scope('AppComponent');
    private readonly translate = inject(TranslateService);
    private readonly appleIapService = inject(AppleIapService);
    private readonly appleSubscriptionAlreadyLinkedCode = 'apple_subscription_already_linked';
    
    version!: string;
    isSidenavOpen = signal(false);
    avatarBoxMode$!: Observable<AvatarBoxMode>;
    isAuthenticated$!: Observable<boolean>;
    isLoggedIn$!: Observable<boolean>;
    isLogoutLoading$!: Observable<boolean>;
    isAuthTransitionLoading$!: Observable<boolean>;

    // Track home route for avatar box display
    isHomeRoute$!: Observable<boolean>;
    isPublicRoute$!: Observable<boolean>;

    // Avatar box state - now comes from NgRx Audio Store
    // NgRx Audio Store is the SINGLE SOURCE OF TRUTH for all avatar box state
    avatarBoxState$!: Observable<AvatarBoxFullState>;

    // Operation guards to prevent duplicates
    private subscriptionCheckCompleted = false;
    private subscriptionLoadCompleted = false;
    private isAppleConflictDialogOpen = false;

    constructor(
        private ngZone: NgZone,
        private authFacade: AuthFacade,
        private userFacade: UserFacade,
        private router: Router,
        private versionService: VersionService,
        private dialog: MatDialog,
        private uiFacade: UiFacade,
        public readonly avatarFacade: AvatarFacade,
        public readonly audioFacade: AudioFacade,
        private audioSessionManager: AudioSessionManagerService,
        private stripeFacade: StripeFacade,
        private feedbackInfrastructure: FeedbackInfrastructureService,
        private notificationFacade: NotificationFacade,
        private notificationService: NotificationService,
        private statusBarService: StatusBarService,
        private toastNotificationService: ToastNotificationService,
        private metaAnalyticsService: MetaAnalyticsService,
    ) {
        // Android uses custom native dispatch via window event
        // iOS uses Capacitor's pushNotificationActionPerformed (handled in mobile-notification.handler.ts)
        if (Capacitor.getPlatform() === 'android') {
            this.setupAndroidNotificationHandler();
        }
    }
    
    /**
     * Android notification handling - receives events from MainActivity.java
     * MainActivity dispatches to window after user taps notification
     */
    private setupAndroidNotificationHandler(): void {
        // Event listener for background state (app was running)
        window.addEventListener('notificationActionFromTerminated', (event: any) => {
            this.ngZone.run(() => {
                const payload = event.detail;
                this.logger.debug('Android notification tap:', { notification_id: payload.notification_id, type: payload.type });
                
                // Clear pending notification
                (window as any).__pendingNotification = null;
                
                this.notificationFacade.notificationTapped(payload);
            });
        });
        
        // Check for pending notification from terminated state
        // MainActivity stores in window.__pendingNotification before Angular bootstraps
        this.checkAndroidPendingNotification();
    }
    
    /**
     * Check for pending notification stored by Android native code during cold start
     */
    private checkAndroidPendingNotification(): void {
        const checkAndHandle = () => {
            const pending = (window as any).__pendingNotification;
            if (pending) {
                this.ngZone.run(() => {
                    this.logger.debug('Android pending notification:', { notification_id: pending.notification_id, type: pending.type });
                    (window as any).__pendingNotification = null;
                    this.notificationFacade.notificationTapped(pending);
                });
                return true;
            }
            return false;
        };
        
        if (checkAndHandle()) return;
        
        // Poll for cold start timing
        [100, 500, 1000, 2000, 3000].forEach(delay => {
            setTimeout(() => checkAndHandle(), delay);
        });
    }

    ngOnInit() {
        this.version = this.versionService.getVersion();
        this.avatarBoxMode$ = this.audioFacade.avatarBoxMode$;
        this.isAuthenticated$ = this.authFacade.isAuthenticated$;
        this.isLoggedIn$ = this.authFacade.isAuthenticated$;
        this.isLogoutLoading$ = this.authFacade.isLogoutLoading$;
        
        // Show loader when authenticated but still on public route (navigating away)
        this.isAuthTransitionLoading$ = combineLatest([
            this.authFacade.isAuthenticated$,
            this.router.events.pipe(
                filter(event => event instanceof NavigationEnd),
                map(() => PUBLIC_ROUTES.some(route => this.router.url.startsWith(route))),
                startWith(PUBLIC_ROUTES.some(route => this.router.url.startsWith(route)))
            )
        ]).pipe(
            map(([isAuth, isPublic]) => isAuth && isPublic),
            distinctUntilChanged()
        );

        // Initialize NgRx auth state - check if user is already authenticated
        this.authFacade.checkInitialAuth();

        // Re-enable Meta tracking for returning users who previously consented
        this.metaAnalyticsService.initializeIfConsented();

        // Initialize status bar (async to wait for platform ready)
        this.statusBarService.initialize().catch(error => {
            this.logger.error('Status bar initialization failed:', error);
        });

        // Re-apply status bar config when app resumes from background
        // This fixes issue where status bar doesn't show on first launch
        // SCRUM-332: Also stop conversation when app becomes inactive (phone call, etc.)
        App.addListener('appStateChange', ({ isActive }) => {
            this.ngZone.run(() => {
                if (isActive) {
                    this.logger.debug('App resumed, re-applying status bar config');
                    this.statusBarService.reapplyConfiguration().catch((error: unknown) => {
                        this.logger.error('Failed to re-apply status bar config:', error);
                    });
                } else {
                    // SCRUM-332: Stop conversation when app becomes inactive
                    // This handles phone calls (ringing or answered), switching apps, etc.
                    // The audio system breaks during phone calls, so we stop proactively
                    this.handleAppInterruption();
                }
            });
        });

        // WEB ONLY: Listen for page visibility change (user returns from external site)
        // This handles the case where user clicks browser back button after opening Stripe portal
        // On native, browserFinished event handles this. On web, we need visibilitychange event.
        if (!Capacitor.isNativePlatform()) {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    this.ngZone.run(() => {
                        this.logger.debug('Page became visible, clearing Stripe loading states');
                        // Clear loading states when user returns to the page
                        // This handles browser back button from Stripe portal/checkout
                        this.stripeFacade.clearExternalNavigationLoadingStates();
                    });
                }
            });
        }

        // Initialize push notifications
        this.initializePushNotifications();

        // Initialize deep link handling for OAuth redirects
        this.initializeDeepLinkHandling();

        // CRITICAL: Listen for in-app browser dismissal on mobile
        // When user closes/dismisses the in-app browser (OAuth, Stripe, etc.), clear loading states
        // This fires on BOTH iOS and Android when Browser.close() is called or user dismisses browser
        // Without this, buttons stay disabled after user cancels OAuth/Stripe flow
        if (Capacitor.isNativePlatform()) {
            Browser.addListener('browserFinished', () => {
                this.ngZone.run(() => {
                    this.logger.debug('In-app browser dismissed');
                    
                    // If user is on login page, clear OAuth loading state
                    if (this.router.url === '/login') {
                        this.logger.debug('Browser dismissed on login page, clearing OAuth loading state');
                        this.authFacade.clearOAuthLoadingState();
                    }
                    
                    // If user is on pricing/billing/plans or checkout status pages, clear Stripe portal/checkout loading states
                    // This handles the case where user presses browser back button instead of completing flow
                    if (this.router.url.startsWith('/billing') || 
                        this.router.url.startsWith('/plans') || 
                        this.router.url.startsWith('/checkout-success') || 
                        this.router.url.startsWith('/checkout-cancel')) {
                        this.logger.debug('Browser dismissed on billing, plans or checkout status page, clearing Stripe loading states');
                        this.stripeFacade.clearExternalNavigationLoadingStates();
                    }
                });
            });
        }

        // Track if we're on home route
        this.isHomeRoute$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => this.router.url === '/home'),
            startWith(this.router.url === '/home'),
            shareReplay(1)
        );

        // Track if we're on a public route (login, register, etc.)
        this.isPublicRoute$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => PUBLIC_ROUTES.some(route => this.router.url.startsWith(route))),
            startWith(PUBLIC_ROUTES.some(route => this.router.url.startsWith(route))),
            shareReplay(1)
        );

        // Track navigation lifecycle to prevent avatar box from appearing during page transitions
        const navigationStartBlock$ = this.router.events.pipe(
            filter(event => event instanceof NavigationStart),
            map(() => false), // Block avatar box when navigation starts
            shareReplay(1)
        );
        
        const navigationEndAllow$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            // Wait 300ms after navigation ends for page to fully render
            switchMap(() => timer(300).pipe(map(() => true))),
            shareReplay(1)
        );

        // Merge navigation events: false during navigation, true after completion + delay
        const canShowAvatarBox$ = merge(
            navigationStartBlock$,
            navigationEndAllow$
        ).pipe(
            startWith(true), // Allow on initial page load
            shareReplay(1)
        );
        
        // Override mode to 'hidden' during navigation transitions (for both fullscreen and minimized)
        this.avatarBoxMode$ = combineLatest([
            this.audioFacade.avatarBoxMode$,
            canShowAvatarBox$
        ]).pipe(
            map(([mode, canShow]) => {
                // Block ANY visible mode during navigation transitions
                if (!canShow && (mode === 'fullscreen' || mode === 'minimized')) {
                    return 'hidden' as AvatarBoxMode;
                }
                return mode;
            }),
            distinctUntilChanged(),
            shareReplay(1)
        );

        this.avatarBoxState$ = this.audioFacade.avatarBoxFullState$;

        // Subscribe to sidenav state from UI store
        this.uiFacade.sidenavOpen$
            .pipe(untilDestroyed(this))
            .subscribe(isOpen => {
                this.isSidenavOpen.set(isOpen);
            });

        // Authentication and subscription check
        this.setupAuthenticationSubscriptions();
        this.setupGlobalAppleConflictPopup();

        // Show inactivity dialog on server-initiated session end and handle feedback
        this.audioSessionManager.sessionEnded$
            .pipe(untilDestroyed(this))
            .subscribe(event => {
                const isPublicRoute = PUBLIC_ROUTES.some(route => this.router.url.startsWith(route));
                if (isPublicRoute) return;

                // Check if this is a trial user who has hit their limit
                // In that case, show activation dialog instead of feedback dialog
                combineLatest([
                    this.stripeFacade.currentSubscription$,
                    this.stripeFacade.realtimeRemainingSeconds$
                ]).pipe(
                    take(1)
                ).subscribe(([subscription, realtimeRemainingSeconds]) => {
                    // Check if trial user hit limit:
                    // - User is on trial
                    // - AND either: can_use_service is false OR realtime remaining is 0 OR event reason indicates limit
                    const isTrialUser = subscription?.is_trial === true;
                    const isLimitReachedReason = event.reason === 'limit_reached' || 
                                                 event.reason === 'time_limit' || 
                                                 event.reason === 'usage_limit';
                    const hasNoTimeLeft = !subscription?.can_use_service || realtimeRemainingSeconds <= 0;
                    const isTrialLimitReached = isTrialUser && (isLimitReachedReason || hasNoTimeLeft);
                    
                    this.logger.debug('Session ended - checking trial status', {
                        isTrialUser,
                        canUseService: subscription?.can_use_service,
                        realtimeRemainingSeconds,
                        eventReason: event.reason,
                        isTrialLimitReached
                    });
                    
                    if (isTrialLimitReached) {
                        // Show activation dialog for trial users who hit their limit
                        this.logger.debug('Trial limit reached - showing activation dialog instead of feedback');
                        this.showTrialActivationDialog(subscription!);
                    } else {
                        // Handle normal feedback flow for all session ends (not just inactivity)
                        this.feedbackInfrastructure.handleSessionEndFeedback(
                            event.sessionId,
                            event.collectFeedback
                        ).pipe(untilDestroyed(this)).subscribe();
                    }

                    // Only show session ended dialog for inactivity timeouts (not for trial limit)
                    if (event.reason === 'inactivity_timeout' && !isTrialLimitReached) {
                        const dialogData: SessionEndedDialogData = {
                            title: this.translate.instant('sessionEndedDialog.title'),
                            message: event.message || this.translate.instant('sessionEndedDialog.message'),
                            iconName: 'info',
                            showRestartOption: true,
                            restartButtonText: this.translate.instant('sessionEndedDialog.restart'),
                            closeButtonText: this.translate.instant('sessionEndedDialog.close')
                        };

                        const dialogRef = this.dialog.open(SessionEndedDialogComponent, {
                            panelClass: 'session-ended-dialog-panel',
                            data: dialogData,
                            disableClose: false
                        });

                        dialogRef.afterClosed().subscribe((result: SessionEndedDialogResult) => {
                            if (result?.action === 'restart') {
                                this.logger.debug('User requested conversation restart from session ended dialog');
                                this.restartConversationFromDialog();
                            } else {
                                this.logger.debug('User closed session ended dialog');
                            }
                        });
                    }
                });
            });
    }

    /**
     * Show activation dialog for trial users who have hit their usage limit
     */
    private showTrialActivationDialog(subscription: UserSubscription): void {
        const dialogRef = this.dialog.open(ActivateSubscriptionDialogComponent, {
            data: {
                context: 'limit_reached',
                planName: subscription?.plan_name
            } as ActivateSubscriptionDialogData,
            width: 'auto',
            maxWidth: '90vw',
            panelClass: 'activate-subscription-dialog-panel',
            disableClose: false
        });

        dialogRef.afterClosed().subscribe((result: ActivateSubscriptionDialogResult | undefined) => {
            if (result?.action === 'activated') {
                this.toastNotificationService.showSuccess(
                    this.translate.instant('toast.subscriptionActivated')
                );
                // User can now continue using the app
            } else {
                // User cancelled - they can still see the app but can't use services
                this.logger.debug('User cancelled trial activation');
            }
        });
    }

    /**
     * SCRUM-332: Handle app interruption (phone call, switching apps, etc.)
     * 
     * On native platforms, stops any active conversation when app becomes inactive.
     * This prevents broken audio state that occurs when:
     * - Phone call starts (ringing or answered)
     * - User switches to another app
     * - System interrupts the app
     * 
     * The audio system (microphone, WebRTC, audio playback) can break during
     * these interruptions, leaving the session in an unusable state.
     * Stopping proactively ensures clean state when user returns.
     */
    private handleAppInterruption(): void {
        // Only handle on native platforms - web handles visibility differently
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        // Check if we have an active conversation
        this.audioFacade.isStreaming$.pipe(take(1)).subscribe(isStreaming => {
            if (isStreaming) {
                this.logger.warn('App became inactive during active conversation - stopping session (SCRUM-332)');
                this.audioFacade.stopConversation();
                
                // Show toast so user knows what happened when they return
                this.toastNotificationService.showInfo(
                    this.translate.instant('toast.conversationStoppedDueToInterruption')
                );
            }
        });
    }

    private setupAuthenticationSubscriptions(): void {
        // Check if user is authenticated and close sidenav for non-authenticated routes
        combineLatest([
            this.authFacade.isAuthenticated$,
            this.authFacade.userBackendSyncCompleted$,
            this.authFacade.initialAuthCheckCompleted$
        ]).pipe(untilDestroyed(this))
            .subscribe(([isAuthenticated, backendSyncCompleted, initialAuthCheckCompleted]) => {
                if (!isAuthenticated) {
                    this.uiFacade.closeSidenav();
                    this.appleIapService.resetLinkedSubscriptionConflict();
                    // Avatar is cleared automatically by NgRx avatar store on logout
                } else if (isAuthenticated && backendSyncCompleted && initialAuthCheckCompleted) {
                    // Load subscription immediately after backend sync to ensure canStart works
                    // Guard against duplicate loads - only load once per session
                    if (!this.subscriptionLoadCompleted) {
                        this.subscriptionLoadCompleted = true;
                        this.stripeFacade.loadUserSubscription();
                    }
                    
                    // Only check subscription status, avatar should load automatically from cache/storage
                    // Guard against duplicate subscription checks
                    if (!this.subscriptionCheckCompleted) {
                        this.subscriptionCheckCompleted = true;
                        // Check subscription status for new users and redirect to pricing if needed
                        this.checkNewUserSubscriptionStatus();
                    }
                }
            });

        // Reset guards when authentication state changes to false
        this.authFacade.isAuthenticated$
            .pipe(untilDestroyed(this))
            .subscribe(isAuthenticated => {
                if (!isAuthenticated) {
                    this.subscriptionCheckCompleted = false;
                    this.subscriptionLoadCompleted = false;
                }
            });
    }

    private setupGlobalAppleConflictPopup(): void {
        if (Capacitor.getPlatform() !== 'ios') {
            return;
        }

        this.appleIapService.iapConflictCode$
            .pipe(
                filter(code => code === this.appleSubscriptionAlreadyLinkedCode),
                untilDestroyed(this)
            )
            .subscribe(() => {
                this.openAppleSubscriptionAlreadyLinkedDialog();
            });
    }

    private openAppleSubscriptionAlreadyLinkedDialog(): void {
        if (this.isAppleConflictDialogOpen) {
            return;
        }
        this.isAppleConflictDialogOpen = true;

        const dialogData: ConfirmationDialogData = {
            title: this.translate.instant('pricing.iap.alreadyLinkedTitle'),
            message: this.translate.instant('pricing.iap.alreadyLinkedMessage'),
            iconName: 'warning_amber',
            confirmButtonText: this.translate.instant('pricing.iap.alreadyLinkedDismiss'),
            cancelButtonText: this.translate.instant('confirmationDialog.cancel'),
            confirmButtonColor: 'primary'
        };

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            panelClass: 'confirmation-dialog-panel',
            maxWidth: '90vw',
            width: 'auto',
            data: dialogData
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
            this.isAppleConflictDialogOpen = false;
        });
    }

    private initializePushNotifications(): void {
        // Initialize notification service
        this.notificationFacade.initialize();
        
        // Wait for initialization, then setup listeners
        this.notificationFacade.isInitialized$
            .pipe(
                filter(isInitialized => isInitialized),
                take(1),
                untilDestroyed(this)
            )
            .subscribe(() => {
                // Setup platform listeners with simplified callbacks
                this.notificationService.setupListeners({
                    onTokenReceived: (token) => {
                        this.ngZone.run(() => this.notificationFacade.tokenReceived(token));
                    },
                    onNotificationReceived: (payload) => {
                        // Foreground notification received
                        this.logger.debug('Notification received (foreground):', payload);

                        if (this.isCheckInNotification(payload)) {
                            this.ngZone.run(() => this.showCheckInNotificationPopup(payload));
                            return;
                        }
                        
                        // On web, show popup directly for foreground notifications
                        // On mobile, native OS shows it, user will tap to see popup
                        if (!Capacitor.isNativePlatform()) {
                            this.ngZone.run(() => this.showNotificationPopup(payload));
                        }
                    },
                    onNotificationTapped: (payload) => {
                        // User tapped notification - route through store for popup or check-in auto-start handling
                        this.ngZone.run(() => this.notificationFacade.notificationTapped(payload));
                    },
                    onRegistrationError: (error) => {
                        this.logger.error('Registration error:', error);
                    }
                });
            });
        
        // When notification is tapped, show the dialog unless the push type has a dedicated flow.
        this.notificationFacade.lastTappedNotification$
            .pipe(
                filter(payload => !!payload),
                untilDestroyed(this)
            )
            .subscribe(payload => {
                if (!this.handleCheckInNotificationTap(payload!)) {
                    this.showNotificationPopup(payload!);
                }
            });
            
        // Setup web terminated state handler
        this.setupWebTerminatedStateHandler();
    }

    /**
     * Show notification popup when user taps a notification
     */
    private showNotificationPopup(payload: NotificationPayload): void {
        if (this.isCheckInNotification(payload)) {
            this.showCheckInNotificationPopup(payload);
            return;
        }

        // Dialog gets buttons based on notification type - no need to pass actions
        const dialogData: NotificationActionDialogData = {
            notification_id: payload.notification_id,
            type: payload.type,
            notification_type: payload.notification_type,
            reminder_category: payload.reminder_category,
            title: payload.title || 'Notification',
            body: payload.body || '',
            route: payload.route
        };
        
        const dialogRef = this.dialog.open(NotificationActionDialogComponent, {
            data: dialogData,
            maxWidth: '90vw',
            width: '480px',
            panelClass: 'notification-action-dialog'
        });
        
        dialogRef.afterClosed().subscribe((result: NotificationActionDialogResult | undefined) => {
            // If popup was closed without clicking a button (clicked outside/backdrop), send skip action
            if (!result) {
                // Skip notification to prevent it staying in Pending status
                if (dialogData.notification_id) {
                    this.notificationFacade.skipNotification(dialogData.notification_id);
                }
                return;
            }
            
            // Validate notification_id before making API call
            if (!result.notification_id) {
                this.logger.error('Cannot handle notification action - missing notification_id');
                this.toastNotificationService.showError('Unable to process action');
                return;
            }
            
            // Handle action based on action_id
            this.notificationFacade.handleNotificationAction(
                result.notification_id,
                result.action_id,
                result.route
            );
        });
    }

    private showCheckInNotificationPopup(payload: NotificationPayload): void {
        const dialogData: NotificationActionDialogData = {
            notification_id: payload.notification_id,
            type: payload.type,
            notification_type: payload.notification_type,
            reminder_category: payload.reminder_category,
            title: payload.title || 'Notification',
            body: payload.body || '',
            route: payload.route,
            actions: [{
                id: 'start_session',
                title: this.translate.instant('notifications.actions.start_session'),
                style: 'primary'
            }]
        };

        const dialogRef = this.dialog.open(NotificationActionDialogComponent, {
            data: dialogData,
            maxWidth: '90vw',
            width: '480px',
            panelClass: 'notification-action-dialog'
        });

        dialogRef.afterClosed().subscribe((result: NotificationActionDialogResult | undefined) => {
            if (result?.action_id === 'start_session') {
                this.startCheckInSession(payload);
            }
        });
    }

    private handleCheckInNotificationTap(payload: NotificationPayload): boolean {
        if (!this.isCheckInNotification(payload)) {
            return false;
        }

        this.startCheckInSession(payload);
        return true;
    }

    private startCheckInSession(payload: NotificationPayload): void {
        const route = this.getNotificationRoute(payload);
        const launchNotificationId = payload.notification_id || undefined;

        if (this.router.url.startsWith('/home')) {
            this.startConversationWhenReady(launchNotificationId);
            return;
        }

        this.router.navigateByUrl(route, {
            state: {
                autoStart: true,
                launchNotificationId
            }
        }).catch(error => {
            this.logger.error('Failed to navigate for check-in notification:', error);
        });
    }

    private getNotificationRoute(payload: NotificationPayload): string {
        return typeof payload.route === 'string' && payload.route ? payload.route : '/home';
    }

    private isCheckInNotification(payload: NotificationPayload): boolean {
        const extraData = this.getNotificationExtraData(payload);
        return payload.notification_type === 'check_in'
            || payload.reminder_category === 'check_in'
            || payload['auto_start_session'] === 'true'
            || extraData?.['notification_type'] === 'check_in'
            || extraData?.['reminder_category'] === 'check_in'
            || extraData?.['auto_start_session'] === 'true';
    }

    private getNotificationExtraData(payload: NotificationPayload): Record<string, any> {
        if (!payload.extra_data) {
            return {};
        }

        if (typeof payload.extra_data === 'string') {
            try {
                return JSON.parse(payload.extra_data);
            } catch (error) {
                this.logger.warn('Failed to parse notification extra_data:', error);
                return {};
            }
        }

        return payload.extra_data;
    }

    private startConversationWhenReady(launchNotificationId?: string): void {
        timer(300).pipe(
            switchMap(() => this.audioFacade.avatarBoxFullState$),
            filter(state => state.canStart && !!state.avatar && !state.isStreaming && !state.isLoading),
            take(1),
            untilDestroyed(this)
        ).subscribe(() => this.audioFacade.startConversation(launchNotificationId));
    }

    /**
     * Handle web terminated state - service worker sends message when app opens from notification
     */
    private setupWebTerminatedStateHandler(): void {
        if (!('serviceWorker' in navigator)) return;
        
        navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data?.type === 'NOTIFICATION_ACTION_FROM_TERMINATED') {
                const payload: NotificationPayload = event.data.data;
                
                // Wait for auth before showing popup
                this.authFacade.isAuthenticated$.pipe(
                    filter(auth => auth),
                    take(1),
                    untilDestroyed(this)
                ).subscribe(() => {
                    this.notificationFacade.notificationTapped(payload);
                });
            }
        });
    }

    /**
     * Initialize deep link handling for OAuth and Stripe redirects
     * This is essential for both Google OAuth and Stripe checkout on mobile devices
     */
    private initializeDeepLinkHandling(): void {
        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            // CRITICAL: Run inside Angular zone to ensure change detection works
            // Without this, toast messages won't show and UI won't update after redirect
            this.ngZone.run(() => {
                this.logger.debug('Deep link received:', event.url);

                // Close in-app browser on deep link
                if (Capacitor.isNativePlatform()) {
                    Browser.close().catch((err: any) => {
                        this.logger.debug('Browser close failed (may already be closed):', err);
                    });
                }

                // Handle OAuth callback from custom URL scheme or HTTPS
                if (event.url.includes('/auth/callback')) {
                    this.logger.debug('OAuth callback detected');

                    // Extract query parameters from the URL
                    let queryParams = '';
                    if (event.url.includes('?')) {
                        queryParams = event.url.substring(event.url.indexOf('?'));
                    }

                    const callbackUrl = '/auth/callback' + queryParams;
                    this.logger.debug('Navigating to:', callbackUrl);

                    // Navigate to /auth/callback with query parameters
                    this.router.navigateByUrl(callbackUrl);
                    return;
                }

                // Handle Stripe checkout callbacks - Backend returns to /billing or /plans route
                // Success: /billing?purchase=success&session_id=cs_xxx
                // Cancel: /billing?purchase=cancel
                if (event.url.includes('/billing?purchase=') || event.url.includes('/plans?purchase=')) {
                    // Extract path and query params
                    let path = '';
                    if (event.url.startsWith('my.greatday.app://')) {
                        // Mobile custom scheme: my.greatday.app://billing?purchase=success&session_id=xxx
                        path = '/' + event.url.replace('my.greatday.app://', '');
                    } else {
                        // Web HTTPS URL
                        try {
                            const url = new URL(event.url);
                            path = url.pathname + url.search;
                        } catch (error) {
                            this.logger.error('Failed to parse checkout URL:', error);
                            // Fallback to home
                            this.router.navigate(['/home']);
                            return;
                        }
                    }
                    
                    this.router.navigateByUrl(path).catch(error => {
                        this.logger.error('Stripe callback navigation failed:', error);
                    });
                    
                    return;
                }

                // Handle other deep links (HTTPS URLs with paths)
                try {
                    // Check if it's a custom scheme
                    if (event.url.startsWith('my.greatday.app://')) {
                        // Extract path from custom scheme: my.greatday.app://path
                        const path = event.url.replace('my.greatday.app://', '/');
                        this.logger.debug('Custom scheme, navigating to path:', path);
                        this.router.navigateByUrl(path);
                        return;
                    }

                    // Handle HTTPS URLs
                    const url = new URL(event.url);
                    const path = url.pathname + url.search + url.hash;

                    this.logger.debug('HTTPS URL, navigating to path:', path);
                    this.router.navigateByUrl(path);
                } catch (error) {
                    this.logger.error('Error parsing deep link URL:', error);

                    // If we can't parse the URL, navigate to home
                    this.logger.warn('Could not parse deep link, navigating to home');
                    this.router.navigate(['/home']);
                }
            });
        });

        this.logger.debug('Deep link handling initialized');
    }

    private async restartConversationFromDialog(): Promise<void> {
        try {
            this.logger.debug('Attempting to restart conversation from dialog');
            this.audioFacade.restartConversation();
            this.logger.debug('Conversation restart requested');
        } catch (error) {
            this.logger.error('Error restarting conversation:', error);
            this.toastNotificationService.showError(this.translate.instant('toast.restartConversationFailed'));
        }
    }

    private checkNewUserSubscriptionStatus(): void {
        // Only check subscription status if user is on protected routes
        // Skip check for public routes like login, registration, etc.
        const currentUrl = this.router.url;
        const isPublicRoute = PUBLIC_ROUTES.some(route => currentUrl.startsWith(route));

        if (isPublicRoute) {
            this.logger.debug('Skipping subscription check on public route:', currentUrl);
            return;
        }

        // Triple-check that user is authenticated and auth check is complete
        if (!this.authFacade.isLoggedInSync()) {
            this.logger.debug('User no longer authenticated, skipping subscription check');
            return;
        }

        // Subscription is already loaded in setupAuthenticationSubscriptions
        // Just check if we need to redirect to pricing for new users
        this.stripeFacade.userSubscription$.pipe(
            filter(subscription => subscription !== null),
            take(1)
        ).subscribe({
            next: (userLimits: UserSubscription | null) => {
                if (userLimits) {
                    this.logger.debug('User subscription status:', userLimits);

                    // Check if user has no subscription/trial or cannot use service
                    const hasActiveSubscription = userLimits.has_subscription && userLimits.is_active;
                    const hasActiveTrial = userLimits.is_trial && userLimits.can_use_service;
                    
                    if (!hasActiveSubscription && !hasActiveTrial) {
                        this.router.navigate(['/plans']);
                    }
                }
            },
            error: (error) => {
                this.logger.error('Error checking user subscription status:', error);
                // If there's an error checking subscription, allow user to continue
                // to avoid blocking access due to network issues
            }
        });
    }

    toggleSidenav(): void {
        this.uiFacade.toggleSidenav();
    }

    closeSidenav(): void {
        this.uiFacade.closeSidenav();
    }

    onMinimizedAvatarClick(): void {
        this.router.navigate(['/home']);
    }

    onAvatarContainerClick(): void {
        // Only navigate if in minimized mode - use firstValueFrom to get current value
        this.audioFacade.avatarBoxMode$.pipe(take(1)).subscribe((mode: AvatarBoxMode) => {
            if (mode === 'minimized') {
                this.onMinimizedAvatarClick();
            }
        });
    }

    // Methods for dialogs can be used by child components if needed
    openDeletePersonalDataDialog(): void {
        const dialogData: ConfirmationDialogData = {
            title: this.translate.instant('deletePersonalDataDialog.title'),
            message: this.translate.instant('deletePersonalDataDialog.message'),
            iconName: 'delete_outline',
            confirmButtonText: this.translate.instant('deletePersonalDataDialog.confirm'),
            confirmButtonColor: 'warn'
        };

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            panelClass: 'confirmation-dialog-panel',
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.logger.debug('User confirmed deletion of personal data.');
                this.userFacade.deletePersonalData();
                this.toastNotificationService.showSuccess(this.translate.instant('toast.personalDataDeletionInitiated'));
            } else {
                this.logger.debug('User cancelled deletion of personal data.');
            }
        });
    }

    openDeleteAccountDialog(): void {
        const dialogData: ConfirmationDialogData = {
            title: this.translate.instant('deleteAccountDialog.title'),
            message: this.translate.instant('deleteAccountDialog.message'),
            iconName: 'delete_forever',
            confirmButtonText: this.translate.instant('deleteAccountDialog.confirm')
        };

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            panelClass: 'confirmation-dialog-panel',
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.logger.debug('User confirmed account deletion. Proceeding with account deletion...');

                // Account deletion will trigger logout automatically via effects after successful deletion
                this.userFacade.deleteAccount();
                this.toastNotificationService.showSuccess(this.translate.instant('toast.accountDeletionInitiated'));
                // Logout will be triggered automatically after successful account deletion
                this.logger.debug('Account deletion dispatched');
            } else {
                this.logger.debug('User cancelled account deletion.');
            }
        });
    }
}